// Indian states → districts (curated JSON) and districts → cities (best-effort
// using country-state-city). Cities are filtered against the state's full
// city list and surfaced with a free-text fallback when the cascading filter
// returns no exact district match.
import { State, City } from "country-state-city";
import data from "./india_states_districts.json";

const STATE_NAMES = data.states.map((s) => s.state).sort((a, b) => a.localeCompare(b));

const districtsByState = Object.fromEntries(
  data.states.map((s) => [s.state, [...s.districts].sort((a, b) => a.localeCompare(b))]),
);

// Build state-name → ISO code map from country-state-city to look up cities.
// Some labels in our districts JSON include "(UT)"/"(NCT)" suffixes — strip them.
const cleanStateName = (n) => n.replace(/\s*\((UT|NCT)\)\s*/i, "").trim();

const cscStates = State.getStatesOfCountry("IN");
const isoByName = {};
for (const st of cscStates) {
  isoByName[st.name.toLowerCase()] = st.isoCode;
}

function citiesOfState(stateName) {
  const iso = isoByName[cleanStateName(stateName).toLowerCase()];
  if (!iso) return [];
  const cities = City.getCitiesOfState("IN", iso);
  const isMaharashtra = cleanStateName(stateName).toLowerCase() === "maharashtra";
  const remapped = cities.map((c) => {
    // Maharashtra-only: Aurangabad city was officially renamed to
    // Chhatrapati Sambhajinagar in 2023. Bihar still has its own Aurangabad.
    if (isMaharashtra && c.name === "Aurangabad") return "Chhatrapati Sambhajinagar";
    return c.name;
  });
  return [...new Set(remapped)].sort((a, b) => a.localeCompare(b));
}

/**
 * Cities filtered to those whose name "starts with" or "contains" the district
 * name. Indian datasets often label cities by their district (e.g. all
 * "Aurangabad … " villages). When no match, fall back to all cities of the state.
 */
function citiesOfDistrict(stateName, districtName) {
  const all = citiesOfState(stateName);
  if (!districtName) return all;
  const d = districtName.toLowerCase();
  const filtered = all.filter((c) => c.toLowerCase().includes(d));
  // Always include the district name itself (most Indian districts share their
  // name with the district HQ city).
  const set = new Set(filtered);
  set.add(districtName);
  // Fall back to the full state city list if the district filter is too narrow
  // (< 5 matches) so users always have a usable selection.
  if (set.size < 5) {
    for (const c of all) set.add(c);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export {
  STATE_NAMES,
  districtsByState,
  citiesOfState,
  citiesOfDistrict,
  cleanStateName,
};
